import { useState, useCallback } from 'react'
import {
  NSIPSFormData,
  NSIPSDrug,
  NSIPSPrescription,
} from '@/features/internal/types/nsips'

const initialDrug: NSIPSDrug = {
  name: '',
  quantity: '',
  unit: '',
}

const initialPrescription: NSIPSPrescription = {
  usage: '',
  days: '',
  drugs: [{ ...initialDrug }],
}

const initialFormData: NSIPSFormData = {
  patientCode: '',
  patientKanaName: '',
  patientKanjiName: '',
  gender: '',
  birthDate: '',
  prescriptions: [{ ...initialPrescription }],
}

export const useNSIPSForm = () => {
  const [formData, setFormData] = useState<NSIPSFormData>(initialFormData)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handlePatientFieldChange = useCallback(
    (field: keyof NSIPSFormData) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value

        setFormData((prev) => ({
          ...prev,
          [field]: value,
        }))
        setErrors((prev) => ({ ...prev, [field]: '' }))
      },
    []
  )

  const handlePrescriptionFieldChange = useCallback(
    (rpIndex: number, field: keyof NSIPSPrescription) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPrescriptions = [...formData.prescriptions]
        newPrescriptions[rpIndex] = {
          ...newPrescriptions[rpIndex],
          [field]: event.target.value,
        }
        setFormData((prev) => ({
          ...prev,
          prescriptions: newPrescriptions,
        }))
        setErrors((prev) => ({ ...prev, [`rp_${rpIndex}_${field}`]: '' }))
      },
    [formData.prescriptions]
  )

  const handleDrugFieldChange = useCallback(
    (rpIndex: number, drugIndex: number, field: keyof NSIPSDrug) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value

        const newPrescriptions = [...formData.prescriptions]
        const newDrugs = [...newPrescriptions[rpIndex].drugs]
        newDrugs[drugIndex] = {
          ...newDrugs[drugIndex],
          [field]: value,
        }
        newPrescriptions[rpIndex] = {
          ...newPrescriptions[rpIndex],
          drugs: newDrugs,
        }
        setFormData((prev) => ({
          ...prev,
          prescriptions: newPrescriptions,
        }))
        setErrors((prev) => ({
          ...prev,
          [`rp_${rpIndex}_drug_${drugIndex}_${field}`]: '',
        }))
      },
    [formData.prescriptions]
  )

  const handleDrugSelect = useCallback(
    (rpIndex: number, drugIndex: number) => (drugName: string) => {
      const newPrescriptions = [...formData.prescriptions]
      const newDrugs = [...newPrescriptions[rpIndex].drugs]
      newDrugs[drugIndex] = {
        ...newDrugs[drugIndex],
        name: drugName,
      }
      newPrescriptions[rpIndex] = {
        ...newPrescriptions[rpIndex],
        drugs: newDrugs,
      }
      setFormData((prev) => ({
        ...prev,
        prescriptions: newPrescriptions,
      }))
      setErrors((prev) => ({
        ...prev,
        [`rp_${rpIndex}_drug_${drugIndex}_name`]: '',
      }))
    },
    [formData.prescriptions]
  )

  const addPrescription = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      prescriptions: [...prev.prescriptions, { ...initialPrescription }],
    }))
  }, [])

  const removePrescription = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      prescriptions: prev.prescriptions.filter((_, i) => i !== index),
    }))
  }, [])

  const addDrug = useCallback((rpIndex: number) => {
    setFormData((prev) => {
      const newPrescriptions = [...prev.prescriptions]
      newPrescriptions[rpIndex] = {
        ...newPrescriptions[rpIndex],
        drugs: [...newPrescriptions[rpIndex].drugs, { ...initialDrug }],
      }
      return {
        ...prev,
        prescriptions: newPrescriptions,
      }
    })
  }, [])

  const removeDrug = useCallback((rpIndex: number, drugIndex: number) => {
    setFormData((prev) => {
      const newPrescriptions = [...prev.prescriptions]
      const newDrugs = newPrescriptions[rpIndex].drugs.filter(
        (_, i) => i !== drugIndex
      )
      newPrescriptions[rpIndex] = {
        ...newPrescriptions[rpIndex],
        drugs: newDrugs,
      }
      return {
        ...prev,
        prescriptions: newPrescriptions,
      }
    })
  }, [])

  const resetForm = useCallback(() => {
    setFormData(initialFormData)
    setErrors({})
  }, [])

  return {
    formData,
    errors,
    setErrors,
    handlePatientFieldChange,
    handlePrescriptionFieldChange,
    handleDrugFieldChange,
    handleDrugSelect,
    addPrescription,
    removePrescription,
    addDrug,
    removeDrug,
    resetForm,
  }
}
